// ABOUTME: Manages wardrobe items, categories, and user's clothing collection state
// ABOUTME: Persists wardrobe data to localStorage for offline access with cloud sync

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getSyncService } from '@/services/SyncService'

export interface WardrobeItem {
  id: string
  name: string
  category: 'tops' | 'bottoms' | 'shoes' | 'outerwear' | 'accessories'
  color: string
  imageData: string // base64 encoded image
  thumbnailData: string // base64 encoded thumbnail
  dateAdded: Date
  tags: string[]
  condition: 'excellent' | 'good' | 'fair'
  formality?: 'casual' | 'smart-casual' | 'formal'
  season?: 'spring' | 'summer' | 'autumn' | 'winter' | 'all-season'
  lastWorn?: string
  timesWorn?: number
}

export interface Outfit {
  id: string
  name: string
  itemIds: string[] // References to WardrobeItem IDs
  dateCreated: Date
  tags: string[]
  occasion?: string
  rating?: number // 1-5 stars
  notes?: string
}

export interface WardrobeStats {
  totalItems: number
  itemsByCategory: Record<string, number>
  mostRecentItem: WardrobeItem | null
}

export interface WardrobeState {
  items: WardrobeItem[]
  stats: WardrobeStats
  outfits: Outfit[]
}

interface WardrobeActions {
  addItem: (item: Omit<WardrobeItem, 'id' | 'dateAdded'>) => WardrobeItem
  removeItem: (itemId: string) => void
  updateItem: (itemId: string, updates: Partial<WardrobeItem>) => void
  getItemsByCategory: (category: WardrobeItem['category']) => WardrobeItem[]
  getStats: () => WardrobeStats
  clearAll: () => void

  // Outfit actions
  createOutfit: (outfit: Omit<Outfit, 'id' | 'dateCreated'>) => Outfit
  updateOutfit: (outfitId: string, updates: Partial<Outfit>) => void
  deleteOutfit: (outfitId: string) => void
  getOutfitItems: (outfitId: string) => WardrobeItem[]
}

type WardrobeStore = WardrobeState & WardrobeActions

const initialState: WardrobeState = {
  items: [],
  outfits: [],
  stats: {
    totalItems: 0,
    itemsByCategory: {
      tops: 0,
      bottoms: 0,
      shoes: 0,
      outerwear: 0,
      accessories: 0,
    },
    mostRecentItem: null,
  },
}

const calculateStats = (items: WardrobeItem[]): WardrobeStats => {
  const itemsByCategory: Record<string, number> = {
    tops: 0,
    bottoms: 0,
    shoes: 0,
    outerwear: 0,
    accessories: 0,
  }

  for (const item of items) {
    itemsByCategory[item.category] = (itemsByCategory[item.category] || 0) + 1
  }

  const mostRecentItem =
    items.length > 0
      ? items.reduce((latest, item) =>
          new Date(item.dateAdded) > new Date(latest.dateAdded) ? item : latest
        )
      : null

  return {
    totalItems: items.length,
    itemsByCategory,
    mostRecentItem,
  }
}

export const useWardrobeStore = create<WardrobeStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      addItem: (item) => {
        const newItem: WardrobeItem = {
          ...item,
          id: crypto.randomUUID(),
          dateAdded: new Date(),
        }

        set((state) => {
          const updatedItems = [...state.items, newItem]
          return {
            items: updatedItems,
            stats: calculateStats(updatedItems),
          }
        })

        // Queue for cloud sync
        getSyncService().queueOperation('wardrobe', 'create', newItem)

        return newItem
      },

      removeItem: (itemId: string) => {
        const itemToRemove = get().items.find((item) => item.id === itemId)

        set((state) => {
          // Delete cloud images if they exist (async, don't block)
          if (itemToRemove) {
            import('@/services/HybridImageStorageService').then(({ getHybridImageStorage }) => {
              const storage = getHybridImageStorage()
              storage.deleteImage(itemToRemove.imageData, itemToRemove.thumbnailData, itemId).catch(console.error)
            })
          }

          const updatedItems = state.items.filter((item) => item.id !== itemId)
          return {
            items: updatedItems,
            stats: calculateStats(updatedItems),
          }
        })

        // Queue for cloud sync
        if (itemToRemove) {
          getSyncService().queueOperation('wardrobe', 'delete', itemToRemove)
        }
      },

      updateItem: (itemId: string, updates: Partial<WardrobeItem>) => {
        let updatedItem: WardrobeItem | undefined

        set((state) => {
          const updatedItems = state.items.map((item) => {
            if (item.id === itemId) {
              updatedItem = { ...item, ...updates }
              return updatedItem
            }
            return item
          })
          return {
            items: updatedItems,
            stats: calculateStats(updatedItems),
          }
        })

        // Queue for cloud sync
        if (updatedItem) {
          getSyncService().queueOperation('wardrobe', 'update', updatedItem)
        }
      },

      getItemsByCategory: (category: WardrobeItem['category']) => {
        return get().items.filter((item) => item.category === category)
      },

      getStats: () => {
        return get().stats
      },

      clearAll: () => {
        set(initialState)
      },

      // Outfit actions
      createOutfit: (outfit) => {
        const newOutfit: Outfit = {
          ...outfit,
          id: crypto.randomUUID(),
          dateCreated: new Date(),
        }

        set((state) => ({
          outfits: [...state.outfits, newOutfit],
        }))

        // Queue for cloud sync
        getSyncService().queueOperation('outfit', 'create', newOutfit)

        return newOutfit
      },

      updateOutfit: (outfitId, updates) => {
        let updatedOutfit: Outfit | undefined

        set((state) => ({
          outfits: state.outfits.map((outfit) => {
            if (outfit.id === outfitId) {
              updatedOutfit = { ...outfit, ...updates }
              return updatedOutfit
            }
            return outfit
          }),
        }))

        // Queue for cloud sync
        if (updatedOutfit) {
          getSyncService().queueOperation('outfit', 'update', updatedOutfit)
        }
      },

      deleteOutfit: (outfitId) => {
        const outfitToDelete = get().outfits.find((o) => o.id === outfitId)

        set((state) => ({
          outfits: state.outfits.filter((outfit) => outfit.id !== outfitId),
        }))

        // Queue for cloud sync
        if (outfitToDelete) {
          getSyncService().queueOperation('outfit', 'delete', outfitToDelete)
        }
      },

      getOutfitItems: (outfitId) => {
        const outfit = get().outfits.find((o) => o.id === outfitId)
        if (!outfit) return []

        const items = get().items
        return outfit.itemIds
          .map((itemId) => items.find((item) => item.id === itemId))
          .filter((item): item is WardrobeItem => item !== undefined)
      },
    }),
    {
      name: 'gentleman-wardrobe-store',
      partialize: (state) => ({
        items: state.items,
        outfits: state.outfits,
      }),
    }
  )
)
